import React, { useState } from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, STATS_FONT_SIZE_LEVELS, DEFAULT_STATS_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { renderFormattedText } from '../utils/formatText';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';
import { MiniStat } from '../components/canvas/Widgets';

/**
 * Normies Burn Memorial layout.
 *
 * Dramatic, full-canvas presentation for burned Normies.
 * Large centered Normie image with a solemn overlay and burn stats.
 * No glass, no rounded corners. Pixelated rendering.
 */
export function NormiesBurnLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const [imgLoaded, setImgLoaded] = useState(false);

  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const bodySize = BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const statsSize = STATS_FONT_SIZE_LEVELS[data.statsFontSize ?? DEFAULT_STATS_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;

  const heroImage = data.normies?.heroImage;
  const stats = data.stats || [];

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  return (
    <>
      {/* Dramatic dark/red overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(20,0,0,0.2) 35%, rgba(20,0,0,0.2) 65%, rgba(0,0,0,0.75) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Kicker — top */}
      <NudgeableGroup groupId="content" className="absolute top-[50px] left-[60px] right-[60px]">
        {data.kickerEnabled !== false && data.kicker && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(kickerSize, 18)}px`,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: '#ef4444',
              textTransform: 'uppercase',
              marginBottom: '12px',
            }}
          >
            {data.kicker}
          </div>
        )}

        {/* Title — large dramatic */}
        <InlineEditable
          value={data.title}
          isEditing={editingField === 'title'}
          onChange={(html) => onEditChange && onEditChange('title', html)}
          onBlur={onEndEdit}
          className={`leading-[0.95] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
          style={{
            fontSize: `${Math.max(titleSize, 64)}px`,
            fontFamily: fontPreset.title,
            fontWeight: fontPreset.titleWeight || 800,
            color: pc.el.title || pc.textPrimary,
            textTransform: 'uppercase',
            letterSpacing: '-0.03em',
          }}
          onClick={handleTitleClick}
        >
          {data.title}
        </InlineEditable>
      </NudgeableGroup>

      {/* Large Normie image — centered memorial portrait (circle) */}
      <div className="absolute inset-0" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '440px',
          height: '440px',
          background: 'rgba(0,0,0,0.3)',
          border: '2px solid rgba(239,68,68,0.25)',
          borderRadius: '50%',
          overflow: 'hidden',
          position: 'relative',
        }}>
          {heroImage ? (
            <img
              src={heroImage}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                imageRendering: 'pixelated',
                opacity: imgLoaded ? 1 : 0,
                transition: 'opacity 0.3s',
              }}
              onLoad={() => setImgLoaded(true)}
              crossOrigin="anonymous"
            />
          ) : (
            <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '13px', color: pc.textSubtle, opacity: 0.5, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>No image</span>
          )}
        </div>
      </div>

      {/* Stats bar */}
      {stats.length > 0 && (
        <div className="absolute" style={{ bottom: '260px', left: '60px', right: '60px' }}>
          <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
            {stats.map((stat, i) => (
              <MiniStat key={i} label={stat.label} value={stat.value} fontSize={statsSize} pc={pc} />
            ))}
          </div>
        </div>
      )}

      {/* Body + Subtitle + Tags — bottom zone */}
      <div className="absolute bottom-[90px] left-[60px] right-[60px]">
        {data.subtitleEnabled !== false && data.subtitle && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(subtitleSize, 18)}px`,
              color: pc.el.subtitle || pc.textMuted,
              textAlign: 'center',
              marginBottom: '10px',
              letterSpacing: '0.04em',
            }}
          >
            {data.subtitle}
          </div>
        )}

        {data.bodyEnabled !== false && data.body && (
          <div
            className="leading-[1.4]"
            style={{
              fontFamily: fontPreset.body || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(bodySize * 0.6, 14)}px`,
              color: pc.el.body || pc.textMuted,
              textAlign: 'center',
              marginBottom: '14px',
              opacity: 0.8,
            }}
          >
            {renderFormattedText(data.body)}
          </div>
        )}

        {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
          <div className="flex flex-wrap gap-3 justify-center">
            {data.meta.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(tagsSize, 12)}px`,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: '#ef4444',
                  border: '1px solid rgba(239,68,68,0.3)',
                  padding: '5px 14px',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-[36px] left-[60px] right-[60px]">
        <SlideFooter data={data} pc={pc} />
      </div>
    </>
  );
}
